<template>
  <div class="tk-root">
    <!-- ═══════════ TOP BAR ═══════════ -->
    <header class="tk-top">
      <div class="tk-live"><span class="dot"></span>EN VIVO</div>
      <div class="tk-stats">
        <div class="stat"><span class="n">{{ stats.enServicio }}</span><span class="l">En servicio</span></div>
        <div class="stat"><span class="n">{{ stats.disponible }}</span><span class="l">Disponibles</span></div>
        <div class="stat"><span class="n">{{ stats.inactivo }}</span><span class="l">Inactivos</span></div>
        <div class="stat"><span class="n">{{ stats.sinConexion }}</span><span class="l">Sin conexión</span></div>
        <div class="stat"><span class="n">{{ stats.total }}</span><span class="l">Total</span></div>
        <div class="tk-clock">{{ clockText }}</div>
      </div>
    </header>

    <!-- ═══════════ ALERTAS ═══════════ -->
    <div class="tk-alerts" v-if="visibleAlerts.length">
      <div v-for="a in visibleAlerts" :key="a.vehicle_id"
        class="tk-alert" :class="a.critical ? 'crit' : 'warn'">
        <i class="ri-alarm-warning-line"></i>
        <div class="msg">
          <b>{{ a.plate }}</b> · {{ a.driver_name }} — {{ a.text }}
          <span v-if="a.critical && a.ride"> · Viaje {{ a.ride.ride_code }} ({{ a.ride.passenger_name }})</span>
        </div>
        <button class="x" title="Descartar" @click="dismissAlert(a.vehicle_id)">&times;</button>
      </div>
    </div>

    <!-- ═══════════ BODY: panel + mapa ═══════════ -->
    <div class="tk-body">
      <aside class="tk-side">
        <div class="tk-search">
          <i class="ri-search-line"></i>
          <input v-model="search" type="text" placeholder="Buscar por nombre o placa" />
        </div>
        <div class="tk-list">
          <div v-for="v in sortedFleet" :key="v.vehicle_id"
            class="tk-unit" :class="{ active: selectedId === v.vehicle_id }"
            @click="selectVehicle(v, true)">
            <div class="av" :style="{ background: hexA(colorOf(v), .14) }" v-html="glyphSVG(v)"></div>
            <div class="main">
              <div class="plate">{{ v.license_plate }}</div>
              <div class="drv" :class="{ 'drv-auto': statusOf(v) === 'auto-cerrado' }">{{ unitSubtitle(v) }}</div>
              <div class="ago" :class="{ off: statusOf(v) === 'sin-conexion' || gpsDisabled(v) }">{{ agoText(v) }}</div>
              <div v-if="isMoving(v)" class="tk-moving">⚠️ En movimiento</div>
            </div>
            <div class="right">
              <span class="badge" :style="badgeStyle(v)">{{ statusLabel(v) }}</span>
              <button v-if="v.driver" type="button" class="tk-route-shift"
                title="Ver recorrido del turno"
                @click.stop="goToRoute(v)">
                <i class="ri-route-line"></i> Ver recorrido
              </button>
              <button v-if="v.driver" type="button" class="tk-close-shift"
                title="Cerrar turno del conductor"
                @click.stop="confirmCloseShift(v)">
                <i class="ri-logout-box-r-line"></i> Cerrar turno
              </button>
            </div>
          </div>
          <div v-if="!sortedFleet.length" class="tk-empty">
            {{ loading ? 'Cargando flota…' : 'Sin vehículos' }}
          </div>
        </div>
        <div class="tk-foot">
          <span>Actualiza cada 30s</span>
          <span class="sync"><span class="d" :class="{ paused }"></span>{{ paused ? 'Pausado' : 'Sincronizado' }}</span>
        </div>
      </aside>

      <div class="tk-map-wrap">
        <div ref="mapContainer" class="tk-map"></div>
        <div class="tk-legend">
          <div class="lt">Estado</div>
          <div class="li"><span class="sw" style="background:#007bff"></span>En camino</div>
          <div class="li"><span class="sw" style="background:#17a2b8"></span>Asignado / Ubicado</div>
          <div class="li"><span class="sw" style="background:#f59e0b"></span>En proceso</div>
          <div class="li"><span class="sw" style="background:#28a745"></span>Disponible</div>
          <div class="li"><span class="sw" style="background:#dc3545"></span>Sin conexión</div>
          <div class="li"><span class="sw" style="background:#6c757d"></span>Inactivo</div>
          <div class="li"><span class="sw" style="background:#6c757d;border:2px solid #f59e0b"></span>Auto-cerrado</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Alert } from 'wize-admin'
import { TrackingService } from '@/core/services'
import { loadGoogleMaps, MAP_ID } from '@/core/utils/googleMaps'

// Centro por defecto (Lima).
const LIMA_CENTER = { lat: -12.046, lng: -77.043 }

// Umbral (metros) para marcar "en movimiento" comparando las últimas 2
// ubicaciones GPS de un vehículo disponible (DEF-ADM-013). Ajustable aquí.
const MOVING_THRESHOLD_M = 200

// Cuánto sigue siendo noticia un auto-cierre. El backend cierra los turnos a
// las 14h abiertos; el aviso sirve para que alguien vea al día siguiente que
// un conductor olvidó cerrar el suyo. Pasado ese plazo deja de ser accionable
// y el vehículo es un inactivo más: sin esto, un turno auto-cerrado hace
// semanas seguía saliendo en ámbar como si fuera de hoy.
const AUTO_CLOSED_WINDOW_MIN = 24 * 60

// Estados VEHÍCULO-céntricos (DEF-ADM-011 v2.0). El color y la etiqueta se
// derivan del turno activo (driver), el viaje (active_ride) y la frescura del GPS.
// `group` agrupa para los contadores del header.
const STATE = {
  'en-camino':    { label: 'En camino',   color: '#007bff', group: 'servicio' },
  'ubicado':      { label: 'Ubicado',     color: '#17a2b8', group: 'servicio' },
  'en-proceso':   { label: 'En proceso',  color: '#f59e0b', group: 'servicio' },
  'asignado':     { label: 'Asignado',    color: '#17a2b8', group: 'servicio' },
  'disponible':   { label: 'Disponible',  color: '#28a745', group: 'disponible' },
  'sin-conexion': { label: 'Sin conexión', color: '#dc3545', group: 'sin-conexion' },
  'inactivo':     { label: 'Inactivo',    color: '#6c757d', group: 'inactivo' },
  // Auto-cerrado: mismo grupo que inactivo (cuenta igual), solo se distingue
  // visualmente en ámbar. Marker gris con borde naranja (ver glyphSVG).
  'auto-cerrado': { label: 'Auto-cerrado', color: '#f59e0b', group: 'inactivo' }
}

function esc(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;')
}

export default {
  name: 'TrackingPage',
  data() {
    return {
      fleet: [],
      loading: false,
      search: '',
      selectedId: null,
      dismissed: {},
      paused: false,
      now: new Date(),
      clockText: '',
      movingIds: {}
    }
  },
  computed: {
    // Timezone IANA de la ciudad activa; el reloj muestra la hora local de esa
    // ciudad. Fallback a America/Lima si no hay ciudad activa.
    cityTz() {
      const city = this.$store.getters['city/getCurrentCity']
      return city && city.timezone ? city.timezone : 'America/Lima'
    },
    stats() {
      const c = { servicio: 0, disponible: 0, 'sin-conexion': 0, inactivo: 0 }
      this.fleet.forEach(v => { c[STATE[this.statusOf(v)].group]++ })
      return {
        enServicio: c.servicio,
        disponible: c.disponible,
        inactivo: c.inactivo,
        sinConexion: c['sin-conexion'],
        total: this.fleet.length
      }
    },
    alerts() {
      const out = []
      this.fleet.forEach(v => {
        if (!v.driver) return // sin turno → no genera alerta
        const disabled = this.gpsDisabled(v)
        const m = this.minutesAgo(v)
        const critical = !!v.active_ride && (disabled || m === null || m > 5)
        if (disabled) {
          // GPS apagado por el conductor: alerta propia (crítica si va en viaje).
          out.push({
            vehicle_id: v.vehicle_id, plate: v.license_plate, driver_name: v.driver.name,
            critical, ride: critical ? v.active_ride : null,
            text: 'GPS desactivado por el conductor'
          })
        } else if (critical) {
          out.push({
            vehicle_id: v.vehicle_id, plate: v.license_plate, driver_name: v.driver.name,
            critical: true, ride: v.active_ride, text: 'Sin GPS con viaje activo'
          })
        } else if (m === null || m > 10) {
          out.push({
            vehicle_id: v.vehicle_id, plate: v.license_plate, driver_name: v.driver.name,
            critical: false, ride: null,
            text: m === null ? 'Sin datos de GPS' : `Última señal hace ${this.durationText(m)}`
          })
        }
      })
      return out
    },
    visibleAlerts() {
      return this.alerts.filter(a => !this.dismissed[a.vehicle_id])
    },
    sortedFleet() {
      const q = this.search.trim().toLowerCase()
      let list = this.fleet
      if (q) {
        list = list.filter(v =>
          (v.license_plate || '').toLowerCase().includes(q) ||
          (v.driver && v.driver.name ? v.driver.name.toLowerCase().includes(q) : false))
      }
      return [...list].sort((a, b) => this.orderKey(a) - this.orderKey(b))
    }
  },
  methods: {
    // ── Estado / helpers (VEHÍCULO-céntrico) ──────────────────────
    hasGPS(v) {
      if (!v.location) return false
      const lat = Number(v.location.latitude), lng = Number(v.location.longitude)
      if (v.location.latitude == null || v.location.longitude == null) return false
      if (Number.isNaN(lat) || Number.isNaN(lng)) return false
      return !(lat === 0 && lng === 0)
    },
    // El conductor apagó el GPS: el último reporte llegó marcado GPS_DISABLED.
    gpsDisabled(v) {
      return !!(v.location && v.location.status === 'GPS_DISABLED')
    },
    minutesAgo(v) {
      if (!v.location || !v.location.last_report_at) return null
      const t = new Date(v.location.last_report_at).getTime()
      if (Number.isNaN(t)) return null
      return (this.now.getTime() - t) / 60000
    },
    // Minutos desde una fecha ISO cualquiera. null si falta o no es válida.
    minutesSince(iso) {
      if (!iso) return null
      const t = new Date(iso).getTime()
      if (Number.isNaN(t)) return null
      return (this.now.getTime() - t) / 60000
    },
    // Minutos que lleva cerrado el turno auto-cerrado, o null si el vehículo no
    // está en ese caso.
    autoClosedMinutes(v) {
      const lcs = v.last_closed_shift
      if (!lcs || lcs.end_type !== 'AUTO_CLOSED') return null
      return this.minutesSince(lcs.ended_at)
    },
    // Sin turno → INACTIVO. Con viaje → estado del viaje. Con turno sin viaje →
    // Disponible (GPS ≤ 10 min) o Sin conexión (GPS > 10 min o ausente).
    statusOf(v) {
      if (!v.driver) {
        // Sin turno activo: el auto-cierre solo se destaca mientras es reciente.
        const closed = this.autoClosedMinutes(v)
        if (closed !== null && closed <= AUTO_CLOSED_WINDOW_MIN) return 'auto-cerrado'
        return 'inactivo'
      }
      const ride = v.active_ride
      if (ride) {
        switch (ride.ride_status) {
          case 'EN_CAMINO':  return 'en-camino'
          case 'UBICADO':    return 'ubicado'
          case 'EN_PROCESO': return 'en-proceso'
          case 'ASIGNADO':   return 'asignado'
          default:           return 'asignado'
        }
      }
      const m = this.minutesAgo(v)
      if (m === null || m > 10) return 'sin-conexion'
      return 'disponible'
    },
    colorOf(v) { return STATE[this.statusOf(v)].color },
    statusLabel(v) { return STATE[this.statusOf(v)].label },
    badgeStyle(v) {
      const c = this.colorOf(v)
      return { color: c, background: this.hexA(c, .14) }
    },
    orderKey(v) {
      if (!v.driver) return 9 // inactivos (sin turno) al fondo
      const m = this.minutesAgo(v)
      const critical = !!v.active_ride && (m === null || m > 5)
      if (critical) return 0
      return {
        'en-camino': 1, 'ubicado': 2, 'en-proceso': 2, 'asignado': 3,
        'disponible': 4, 'sin-conexion': 5, 'inactivo': 9
      }[this.statusOf(v)]
    },
    // Subtítulo de la unidad en el panel: conductor, o aviso si es auto-cerrado.
    unitSubtitle(v) {
      if (this.statusOf(v) === 'auto-cerrado') return '⚠️ Turno cerrado automáticamente'
      return v.driver ? v.driver.name : 'Sin conductor'
    },
    // Duración legible. Por encima de la hora se pasa a horas: un turno cerrado
    // ayer decía "1380 min", que nadie lee de un vistazo.
    durationText(minutes) {
      if (minutes < 60) return `${Math.round(minutes)} min`
      return `${Math.round(minutes / 60)} h`
    },
    elapsedText(minutes) {
      if (minutes < 1) return 'Justo ahora'
      return `Hace ${this.durationText(minutes)}`
    },
    agoText(v) {
      // GPS apagado por el conductor: prima sobre el "hace X min".
      if (this.gpsDisabled(v)) return 'GPS desactivado'
      // Auto-cerrado: mostrar cuánto hace que se cerró el turno (no el GPS).
      if (this.statusOf(v) === 'auto-cerrado') {
        const m = this.autoClosedMinutes(v)
        if (m === null) return 'Cerrado'
        return this.elapsedText(m)
      }
      if (!v.driver) return 'Sin turno'
      const m = this.minutesAgo(v)
      if (m === null) return 'Sin datos'
      return this.elapsedText(m)
    },
    hexA(hex, a) {
      const h = hex.replace('#', '')
      const r = parseInt(h.substr(0, 2), 16), g = parseInt(h.substr(2, 2), 16), b = parseInt(h.substr(4, 2), 16)
      return `rgba(${r},${g},${b},${a})`
    },
    // Distancia en metros (Haversine) entre dos coordenadas.
    distanceMeters(lat1, lng1, lat2, lng2) {
      const R = 6371000
      const rad = Math.PI / 180
      const dLat = (lat2 - lat1) * rad
      const dLng = (lng2 - lng1) * rad
      const a = Math.sin(dLat / 2) ** 2 +
        Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(dLng / 2) ** 2
      return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    },
    // Detecta "en movimiento" comparando la ubicación actual con la del refresh
    // anterior (guardada en memoria). Solo aplica a vehículos disponibles.
    // No requiere cambios en el backend (DEF-ADM-013, alternativa frontend).
    updateMoving() {
      const next = {}
      const moving = {}
      this.fleet.forEach(v => {
        if (!this.hasGPS(v)) return
        const lat = Number(v.location.latitude), lng = Number(v.location.longitude)
        next[v.vehicle_id] = { lat, lng }
        const prev = this.prevLoc[v.vehicle_id]
        if (prev && this.distanceMeters(prev.lat, prev.lng, lat, lng) > MOVING_THRESHOLD_M) {
          moving[v.vehicle_id] = true
        }
      })
      this.prevLoc = next
      this.movingIds = moving
    },
    isMoving(v) {
      return this.statusOf(v) === 'disponible' && !!this.movingIds[v.vehicle_id]
    },
    carSVG(fill, stroke = '#0B0E14', sw = 1.2) {
      return `<svg viewBox="0 0 24 24" fill="none"><path d="M12 2.5L20 20 12 16 4 20z" fill="${fill}" stroke="${stroke}" stroke-width="${sw}" stroke-linejoin="round"/></svg>`
    },
    // Vehículo de un proveedor ASOCIADO (DEF-ADM-018 §4.7). Se basa en el
    // vehículo, no en el turno: un asociado sin turno igual se distingue.
    // Los de transportista no llegan al mapa (el backend los excluye).
    isAsociado(v) {
      return v.provider_type === 'ASOCIADO'
    },
    // Glyph del vehículo. Auto-cerrado: gris con borde naranja para distinguirlo
    // del inactivo (gris con borde oscuro). Asociado: borde verde.
    glyphSVG(v) {
      if (this.statusOf(v) === 'auto-cerrado') return this.carSVG('#6c757d', '#f59e0b', 2.2)
      if (this.isAsociado(v)) return this.carSVG(this.colorOf(v), '#28a745', 2.2)
      return this.carSVG(this.colorOf(v))
    },

    // ── Alertas ───────────────────────────────────────────────────
    dismissAlert(id) { this.$set(this.dismissed, id, true) },

    // ── Ver recorrido del turno activo (DEF-ADM-013) ──────────────
    goToRoute(v) {
      if (!v.driver || !v.driver.shift_id) return
      this.$router.push({ name: 'shift-route', params: { id: v.driver.shift_id } })
    },

    // ── Cerrar turno desde el admin ───────────────────────────────
    async confirmCloseShift(v) {
      if (!v.driver) return
      const ok = window.confirm(
        `¿Cerrar el turno de ${v.driver.name} en el vehículo ${v.license_plate}?\n\n` +
        'Se le notificará por push al conductor.'
      )
      if (!ok) return
      try {
        await TrackingService.forceCloseShift(v.driver.shift_id)
        Alert.success('Turno cerrado correctamente')
        await this.fetchFleet()
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'No se pudo cerrar el turno')
      }
    },

    // ── Data ──────────────────────────────────────────────────────
    async fetchFleet() {
      this.loading = true
      try {
        const res = await TrackingService.getFleet()
        this.fleet = res.payload || []
        // Las alertas descartadas reaparecen si el vehículo sigue en falla.
        this.dismissed = {}
        // Compara con las ubicaciones del refresh previo para marcar movimiento.
        this.updateMoving()
        this.updateMarkers()
        // Primer render: encuadrar todos los markers de golpe.
        if (!this.didFit) {
          this.fitToMarkers()
          this.didFit = true
        }
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al obtener la flota')
      } finally {
        this.loading = false
      }
    },

    // ── Mapa ──────────────────────────────────────────────────────
    async initMap() {
      const { maps, marker, core } = await loadGoogleMaps()
      this.maps = maps
      this.marker = marker
      this.core = core
      this.map = new maps.Map(this.$refs.mapContainer, {
        center: LIMA_CENTER,
        zoom: 12,
        mapId: MAP_ID,
        colorScheme: 'DARK',
        disableDefaultUI: false,
        zoomControl: true,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true
      })
      // InfoWindow compartida (una sola instancia reutilizada).
      this.infoWindow = new maps.InfoWindow()
    },
    // Ajusta la altura del contenedor para llenar desde su tope hasta el fondo
    // del viewport, sin números mágicos (funciona con cualquier alto de header).
    adjustSize() {
      const el = this.$el
      if (!el || !el.getBoundingClientRect) return
      const top = el.getBoundingClientRect().top
      el.style.height = Math.max(400, Math.round(window.innerHeight - top)) + 'px'
      // Google Maps recalcula el tamaño del contenedor automáticamente.
    },
    // Encaja el mapa para mostrar todos los markers visibles de golpe.
    fitToMarkers() {
      if (!this.map || !this.core) return
      const pts = this.fleet.filter(v => this.hasGPS(v))
      if (pts.length) {
        const bounds = new this.core.LatLngBounds()
        pts.forEach(v => bounds.extend({
          lat: Number(v.location.latitude), lng: Number(v.location.longitude)
        }))
        // Padding con más margen a la izquierda por el panel lateral (~340px).
        this.map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 380 })
        // fitBounds no acepta maxZoom: clampeamos una sola vez tras encuadrar.
        this.core.event.addListenerOnce(this.map, 'idle', () => {
          if (this.map.getZoom() > 16) this.map.setZoom(16)
        })
      } else {
        this.map.setCenter(LIMA_CENTER)
        this.map.setZoom(12)
      }
    },
    // Contenido DOM del AdvancedMarker: mismo HTML que el divIcon de Leaflet
    // (halo + glyph SVG + label placa). Reusa las clases .tk-car de siempre.
    buildMarkerContent(v) {
      const color = this.colorOf(v)
      const label = esc(v.license_plate)
      const el = document.createElement('div')
      el.className = 'tk-car'
      // AdvancedMarker ancla el contenido por su borde inferior-centro; con
      // translateY(50%) centramos el glyph sobre la coordenada (como iconAnchor).
      el.style.transform = 'translateY(50%)'
      el.innerHTML = `
        <div class="halo" style="background:${this.hexA(color, .35)}"></div>
        <div class="glyph">${this.glyphSVG(v)}</div>
        <div class="tk-car-label">${label}</div>`
      return el
    },
    popupHTML(v) {
      const st = STATE[this.statusOf(v)]
      const drv = v.driver
      const ride = v.active_ride
      const driverLine = drv
        ? `${esc(drv.name)}${v.vehicle_type ? ' · ' + esc(v.vehicle_type) : ''}`
        : `Sin conductor${v.vehicle_type ? ' · ' + esc(v.vehicle_type) : ''}`
      const phone = drv && drv.phone
        ? `<div class="prow"><span>Teléfono</span><b><a href="tel:${esc(drv.phone)}" style="color:${st.color}">${esc(drv.phone)}</a></b></div>`
        : ''
      const rideRows = ride
        ? `<div class="prow"><span>Viaje</span><b>${esc(ride.ride_code)}</b></div>
           <div class="prow"><span>Pasajero</span><b>${esc(ride.passenger_name)}</b></div>
           <div class="prow"><span>Destino</span><b>${esc(ride.destination)}</b></div>`
        : ''
      // Vehículo de un asociado: se indica en el popup (DEF-ADM-018).
      const providerRow = this.isAsociado(v)
        ? `<div class="prow"><span>Proveedor</span><b style="color:#28a745">Asociado</b></div>`
        : ''
      return `<div class="tk-pop">
        <h3>${esc(v.license_plate)}</h3>
        <div class="pd">${driverLine}</div>
        <div class="prow"><span>Estado</span><b style="color:${st.color}">${st.label}</b></div>
        ${providerRow}
        ${phone}
        <div class="prow"><span>Último GPS</span><b>${esc(this.agoText(v))}</b></div>
        ${rideRows}
      </div>`
    },
    // Interpola la posición del AdvancedMarker entre su punto previo (__pos) y el
    // nuevo, seteando marker.position por frame (equivalente al setLatLng de Leaflet).
    animateMarker(marker, to) {
      const from = marker.__pos || to
      const start = performance.now()
      const dur = 600
      const step = (t) => {
        const p = Math.min((t - start) / dur, 1)
        marker.position = {
          lat: from.lat + (to.lat - from.lat) * p,
          lng: from.lng + (to.lng - from.lng) * p
        }
        if (p < 1) {
          marker.__anim = requestAnimationFrame(step)
        } else {
          marker.__pos = to
        }
      }
      if (marker.__anim) cancelAnimationFrame(marker.__anim)
      marker.__anim = requestAnimationFrame(step)
    },
    updateMarkers() {
      if (!this.map || !this.marker) return
      const seen = new Set()
      // Solo vehículos con GPS tienen marker; sin location → solo en panel.
      this.fleet.filter(v => this.hasGPS(v)).forEach(v => {
        seen.add(v.vehicle_id)
        const pos = { lat: Number(v.location.latitude), lng: Number(v.location.longitude) }
        let m = this.markers[v.vehicle_id]
        if (!m) {
          m = new this.marker.AdvancedMarkerElement({
            map: this.map,
            position: pos,
            content: this.buildMarkerContent(v),
            title: v.license_plate,
            gmpClickable: true
          })
          m.__pos = pos
          m.addListener('gmp-click', () => this.selectVehicle(v, false))
          this.markers[v.vehicle_id] = m
        } else {
          // Refrescar el contenido (color/estado) y animar hacia la nueva posición.
          m.content = this.buildMarkerContent(v)
          this.animateMarker(m, pos)
        }
      })
      // Quitar markers de vehículos que ya no reportan GPS.
      Object.keys(this.markers).forEach(id => {
        if (!seen.has(Number(id))) {
          if (this.markers[id].__anim) cancelAnimationFrame(this.markers[id].__anim)
          this.markers[id].map = null
          delete this.markers[id]
        }
      })
    },
    selectVehicle(v, fromPanel) {
      this.selectedId = v.vehicle_id
      const m = this.markers[v.vehicle_id]
      if (!m) {
        // Sin GPS → no está en el mapa; solo se resalta en el panel.
        if (fromPanel && Alert.info) Alert.info('Este vehículo no reporta GPS')
        return
      }
      const pos = m.__pos || m.position
      if (fromPanel && pos) {
        this.map.panTo(pos)
        this.map.setZoom(15)
      }
      this.infoWindow.setContent(this.popupHTML(v))
      this.infoWindow.open({ map: this.map, anchor: m })
    },

    // ── Ciclo de refresco ─────────────────────────────────────────
    tickClock() {
      this.now = new Date()
      this.clockText = this.now.toLocaleTimeString('es-PE', { timeZone: this.cityTz, hour12: false })
    },
    handleVisibility() {
      this.paused = document.hidden
      if (!document.hidden) this.fetchFleet()
    }
  },
  created() {
    // Objetos del mapa: NO reactivos (fuera de data()).
    this.maps = null
    this.marker = null
    this.core = null
    this.map = null
    this.infoWindow = null
    this.markers = {}
    this.refreshTimer = null
    this.clockTimer = null
    this.didFit = false
    // Ubicaciones del refresh anterior (para detectar movimiento). NO reactivo.
    this.prevLoc = {}
  },
  async mounted() {
    // Marca para los overrides de layout dark, limitados al área de contenido.
    document.body.classList.add('tracking-active')
    await this.initMap()
    this.fetchFleet()
    this.tickClock()
    this.clockTimer = setInterval(this.tickClock, 1000)
    this.refreshTimer = setInterval(() => {
      if (!document.hidden) this.fetchFleet()
    }, 30000)
    document.addEventListener('visibilitychange', this.handleVisibility)
    window.addEventListener('resize', this.adjustSize)
    // Colapsar el sidebar del admin (tras el mount de PageBase) y medir el alto.
    this.$nextTick(() => {
      this.$store.commit('app/setMenu', false)
      this.adjustSize()
      setTimeout(this.adjustSize, 850) // tras la transición padding-left del sidebar
    })
  },
  beforeDestroy() {
    document.body.classList.remove('tracking-active')
    if (this.refreshTimer) clearInterval(this.refreshTimer)
    if (this.clockTimer) clearInterval(this.clockTimer)
    document.removeEventListener('visibilitychange', this.handleVisibility)
    window.removeEventListener('resize', this.adjustSize)
    // Restaurar el sidebar al salir de tracking.
    this.$store.commit('app/setMenu', true)
    Object.values(this.markers).forEach(m => {
      if (m.__anim) cancelAnimationFrame(m.__anim)
      m.map = null
    })
    this.markers = {}
    if (this.infoWindow) this.infoWindow.close()
    // Google Maps no expone destroy(); soltamos las referencias.
    this.infoWindow = null
    this.map = null
  }
}
</script>

<!-- Estilos dark namespaced bajo .tk-root / .tk-map (NO scoped: los markers se
     inyectan por JS y deben poder estilizarse; el prefijo tk- evita fugas al admin light). -->
<style lang="stylus">
.tk-root
  --tk-base #0B0E14
  --tk-surface #141922
  --tk-surface-2 #1B2230
  --tk-border #232C3B
  --tk-border-soft #1C2431
  --tk-text #E7ECF3
  --tk-muted #8592A6
  --tk-muted-2 #5C6675
  --tk-accent #F5A524
  --tk-green #25C26E
  position relative
  display flex
  flex-direction column
  height 100%
  width 100%
  background var(--tk-base)
  color var(--tk-text)
  overflow hidden
  font-family 'Inter', system-ui, sans-serif

  *
    box-sizing border-box

  // ---- Top bar ----
  .tk-top
    display flex
    align-items center
    gap 18px
    padding 0 18px
    height 56px
    flex-shrink 0
    background var(--tk-surface)
    border-bottom 1px solid var(--tk-border)

  .tk-live
    display flex
    align-items center
    gap 8px
    font-size 12px
    font-weight 700
    letter-spacing .14em
    color var(--tk-green)
    padding 5px 11px
    border-radius 999px
    background rgba(37,194,110,.10)
    border 1px solid rgba(37,194,110,.25)
    .dot
      width 8px
      height 8px
      border-radius 50%
      background var(--tk-green)
      animation tkPulse 1.8s infinite

  .tk-stats
    margin-left auto
    display flex
    align-items center
    gap 22px
    .stat
      display flex
      flex-direction column
      line-height 1.1
      .n
        font-size 19px
        font-weight 700
      .l
        font-size 10px
        color var(--tk-muted)
        text-transform uppercase
        letter-spacing .09em
        margin-top 2px
  .tk-clock
    font-size 14px
    font-weight 600
    font-variant-numeric tabular-nums
    padding-left 20px
    border-left 1px solid var(--tk-border)

  // ---- Alerts ----
  .tk-alerts
    flex-shrink 0
    display flex
    flex-direction column
    gap 6px
    padding 10px 14px
    background var(--tk-surface)
    border-bottom 1px solid var(--tk-border)
    max-height 148px
    overflow-y auto
  .tk-alert
    display flex
    align-items center
    gap 10px
    padding 8px 12px
    border-radius 9px
    font-size 13px
    border 1px solid transparent
    i
      font-size 16px
    .msg
      flex 1
      min-width 0
    .x
      background none
      border none
      color inherit
      font-size 18px
      line-height 1
      cursor pointer
      opacity .7
      &:hover
        opacity 1
    &.warn
      background rgba(245,158,11,.10)
      border-color rgba(245,158,11,.35)
      color #F7BC5A
    &.crit
      background rgba(239,68,68,.12)
      border-color rgba(239,68,68,.45)
      color #F98A8A
      animation tkBlink 1.1s ease-in-out infinite

  // ---- Body ----
  .tk-body
    flex 1
    display flex
    min-height 0

  // ---- Sidebar ----
  .tk-side
    width 340px
    flex-shrink 0
    display flex
    flex-direction column
    background var(--tk-surface)
    border-right 1px solid var(--tk-border)
    min-height 0
  .tk-search
    position relative
    padding 12px 14px
    border-bottom 1px solid var(--tk-border-soft)
    i
      position absolute
      left 24px
      top 50%
      transform translateY(-50%)
      color var(--tk-muted-2)
      font-size 15px
    input
      width 100%
      background var(--tk-surface-2)
      border 1px solid var(--tk-border)
      border-radius 9px
      padding 8px 12px 8px 32px
      color var(--tk-text)
      font-size 13px
      outline none
      &::placeholder
        color var(--tk-muted-2)
      &:focus
        border-color var(--tk-accent)
  .tk-list
    flex 1
    overflow-y auto
    padding 8px
    &::-webkit-scrollbar
      width 8px
    &::-webkit-scrollbar-thumb
      background var(--tk-border)
      border-radius 8px
  .tk-unit
    display grid
    grid-template-columns auto 1fr auto
    gap 11px
    align-items center
    padding 11px
    border-radius 11px
    cursor pointer
    border 1px solid transparent
    margin-bottom 4px
    transition background .15s, border-color .15s
    &:hover
      background var(--tk-surface-2)
    &.active
      background var(--tk-surface-2)
      border-color var(--tk-accent)
    .av
      width 38px
      height 38px
      border-radius 10px
      display flex
      align-items center
      justify-content center
      flex-shrink 0
      svg
        width 21px
        height 21px
    .main
      min-width 0
      .plate
        font-weight 600
        font-size 14px
      .drv
        font-size 12px
        color var(--tk-muted)
        white-space nowrap
        overflow hidden
        text-overflow ellipsis
        margin-top 1px
        &.drv-auto
          color #f59e0b
          font-weight 600
      .ago
        font-size 11px
        color var(--tk-muted-2)
        margin-top 3px
        &.off
          color #EF6A6A
      .tk-moving
        font-size 11px
        font-weight 600
        color #F7BC5A
        margin-top 3px
    .right
      text-align right
      .badge
        font-size 10px
        font-weight 600
        letter-spacing .03em
        text-transform uppercase
        padding 3px 8px
        border-radius 999px
        white-space nowrap
      .tk-route-shift
        display block
        margin-top 6px
        margin-left auto
        font-size 10px
        font-weight 600
        white-space nowrap
        color #7FB2FF
        background rgba(0,123,255,.12)
        border 1px solid rgba(0,123,255,.35)
        border-radius 6px
        padding 3px 7px
        cursor pointer
        &:hover
          background rgba(0,123,255,.22)
          color #fff
      .tk-close-shift
        display block
        margin-top 6px
        margin-left auto
        font-size 10px
        font-weight 600
        white-space nowrap
        color #F98A8A
        background rgba(239,68,68,.12)
        border 1px solid rgba(239,68,68,.35)
        border-radius 6px
        padding 3px 7px
        cursor pointer
        &:hover
          background rgba(239,68,68,.22)
          color #fff
  .tk-empty
    text-align center
    color var(--tk-muted-2)
    font-size 13px
    padding 30px 12px
  .tk-foot
    flex-shrink 0
    padding 11px 16px
    border-top 1px solid var(--tk-border-soft)
    font-size 11px
    color var(--tk-muted-2)
    display flex
    justify-content space-between
    align-items center
    .sync
      display flex
      align-items center
      gap 6px
      .d
        width 6px
        height 6px
        border-radius 50%
        background var(--tk-green)
        &.paused
          background var(--tk-muted-2)

  // ---- Map ----
  .tk-map-wrap
    flex 1
    position relative
    min-width 0
  .tk-map
    position absolute
    inset 0
    background var(--tk-base)
  .tk-legend
    position absolute
    bottom 14px
    left 14px
    z-index 500
    background rgba(20,25,34,.92)
    border 1px solid var(--tk-border)
    border-radius 11px
    padding 11px 13px
    .lt
      font-size 10px
      text-transform uppercase
      letter-spacing .1em
      color var(--tk-muted)
      margin-bottom 7px
    .li
      display flex
      align-items center
      gap 8px
      font-size 12px
      padding 2px 0
      .sw
        width 9px
        height 9px
        border-radius 50%

// ---- Google Maps InfoWindow (dark, contenido bajo .tk-root) ----
.tk-root .tk-map
  .gm-style-iw-c
    background #141922 !important
    border 1px solid #232C3B
    border-radius 12px !important
    box-shadow 0 6px 20px rgba(0,0,0,.5) !important
    padding 0 !important
  .gm-style-iw-d
    overflow hidden !important
    color #E7ECF3
  .gm-style-iw-tc::after
    background #141922 !important
  .gm-ui-hover-effect > span
    background-color #8592A6 !important

// ---- Marcador (HTML inyectado por JS, contenido bajo .tk-root) ----
.tk-root .tk-car
  will-change transform
  .halo
    position absolute
    top 50%
    left 50%
    width 34px
    height 34px
    margin -17px 0 0 -17px
    border-radius 50%
    animation tkHalo 2.4s ease-out infinite
  .glyph
    position relative
    width 28px
    height 28px
    display flex
    align-items center
    justify-content center
    filter drop-shadow(0 2px 4px rgba(0,0,0,.5))
    svg
      width 28px
      height 28px
.tk-root .tk-car-label
  position absolute
  top 30px
  left 50%
  transform translateX(-50%)
  font-size 10px
  font-weight 600
  background rgba(11,14,20,.9)
  border 1px solid #232C3B
  padding 1px 6px
  border-radius 6px
  white-space nowrap
  color #E7ECF3

// ---- Popup interno (contenido bajo .tk-root) ----
.tk-root .tk-pop
  h3
    font-size 15px
    margin-bottom 2px
  .pd
    font-size 12px
    color #8592A6
    margin-bottom 8px
  .prow
    display flex
    justify-content space-between
    gap 16px
    font-size 12px
    padding 3px 0
    border-top 1px solid #1C2431
    span:first-child
      color #8592A6

@keyframes tkPulse
  0%
    box-shadow 0 0 0 0 rgba(37,194,110,.5)
  70%
    box-shadow 0 0 0 9px rgba(37,194,110,0)
  100%
    box-shadow 0 0 0 0 rgba(37,194,110,0)
@keyframes tkHalo
  0%
    transform scale(.4)
    opacity .55
  100%
    transform scale(1.5)
    opacity 0
@keyframes tkBlink
  0%, 100%
    opacity 1
  50%
    opacity .55

@media (prefers-reduced-motion: reduce)
  .tk-root .tk-car .halo, .tk-root .tk-live .dot, .tk-root .tk-alert.crit
    animation none

// ---- Layout: tracking llena el área de contenido de PageBase ----
// Gated por body.tracking-active (se agrega/quita en mount/destroy). Solo toca
// los contenedores de contenido; el sidebar y el header del admin NO se alteran.
body.tracking-active .wrapper-page__content
  min-height 0 !important
  padding-bottom 0 !important
  overflow hidden !important
body.tracking-active footer
  display none !important
</style>
